/**
 * Cart Drawer Recommendations Component
 *
 * A component that displays product recommendations in the cart drawer.
 * This integrates directly with the Vue instance in the right drawer.
 */

const CartDrawerRecommendations = {
  props: {
    limit: {
      type: Number,
      default: 4,
    },
    title: {
      type: String,
      default: 'You might also like',
    },
    showVendor: {
      type: Boolean,
      default: false,
    },
    showPrice: {
      type: Boolean,
      default: true,
    },
    minCartValue: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      loading: true,
      recommendations: [],
      lastProductId: null,
      lastProductIds: [],
      lastCartData: null,
      addingToCart: null,
      translations: {
        recommendations: 'You might also like',
        loading: 'Loading...',
        noRecommendations: 'No recommendations available',
        addingToCart: 'Adding...',
        addToCart: 'Add to cart',
        soldOut: 'Sold out',
      },
    };
  },

  computed: {
    hasRecommendations() {
      return this.recommendations && this.recommendations.length > 0;
    },

    cartTotal() {
      if (this.$parent && this.$parent.cart && this.$parent.cart.total_price) {
        return this.$parent.cart.total_price / 100;
      }
      return 0;
    },

    shouldShowRecommendations() {
      return this.hasRecommendations && this.cartTotal >= this.minCartValue;
    },
  },

  template: `
    <div class="cart-drawer-recommendations-wrapper" v-if="shouldShowRecommendations">
      <div class="cart-drawer-recommendations">
        <h3 class="cart-drawer-recommendations__title">{{ title || translations.recommendations }}</h3>
        <div class="cart-drawer-recommendations__container">
          <div class="cart-drawer-recommendations__products" v-if="!loading && recommendations.length > 0">
            <div class="cart-drawer-recommendations__rows">
              <div v-for="product in recommendations" :key="product.id" class="cart-drawer-recommendations__row">
                <div class="cart-drawer-recommendations__row-inner">
                  <div class="cart-drawer-recommendations__product-image">
                    <a :href="product.url">
                      <img :src="product.featured_image" :alt="product.title">
                    </a>
                  </div>
                  <div class="cart-drawer-recommendations__product-info">
                    <h4 class="cart-drawer-recommendations__product-title">
                      <a :href="product.url">{{ product.title }}</a>
                    </h4>
                    <div v-if="showVendor && product.vendor" class="cart-drawer-recommendations__product-vendor">
                      {{ product.vendor }}
                    </div>
                    <div v-if="showPrice" class="cart-drawer-recommendations__product-price" v-html="product.price_html"></div>
                    <button
                      class="cart-drawer-recommendations__product-button btn btn--secondary"
                      @click="addToCart(product.variant_id, 1)"
                      :disabled="addingToCart === product.variant_id || !product.variant_id || !product.available"
                    >
                      <span v-if="addingToCart === product.variant_id">
                        {{ translations.addingToCart }}
                      </span>
                      <span v-else-if="!product.available">
                        {{ translations.soldOut || 'Sold out' }}
                      </span>
                      <span v-else>
                        {{ translations.addToCart }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="cart-drawer-recommendations__loading" v-if="loading">
            <div class="loading-state-icon">
              <i class="fa fa-circle-notch fa-spin"></i>
              <span class="sr-only">{{ translations.loading }}</span>
            </div>
          </div>
          <div v-if="!loading && recommendations.length === 0" class="cart-drawer-recommendations__empty">
            <p>{{ translations.noRecommendations }}</p>
          </div>
        </div>
      </div>
    </div>
  `,

  methods: {
    fetchRecommendations() {
      if (!this.lastCartData || !this.lastCartData.items || this.lastCartData.items.length === 0) {
        this.loading = false;
        this.recommendations = [];
        return;
      }

      const lastItem = this.lastCartData.items[0];
      const productId = lastItem.product_id;
      // Determine if cart items have changed (by product IDs)
      const currentProductIds = this.lastCartData.items.map(item => item.product_id).sort();
      if (JSON.stringify(currentProductIds) === JSON.stringify(this.lastProductIds) && this.recommendations.length > 0) {
        this.loading = false;
        return;
      }
      // Update cached product IDs
      this.lastProductIds = currentProductIds;
      this.loading = true;

      fetch(`/recommendations/products.json?product_id=${productId}&limit=${this.limit}`)
        .then((response) => response.json())
        .then((data) => {
          if (data.products && data.products.length > 0) {
            const productPromises = data.products.map((product) => {
              return fetch(`/products/${product.handle}.js`)
                .then((response) => response.json())
                .then((fullProduct) => {
                  const firstVariant =
                    fullProduct.variants && fullProduct.variants.length > 0 ? fullProduct.variants[0] : null;

                  return {
                    ...product,
                    variant_id: firstVariant ? firstVariant.id : null,
                    price_html: this.formatMoney(product.price),
                    available: firstVariant ? firstVariant.available : false,
                  };
                })
                .catch((error) => {
                  console.error(`Error fetching product ${product.handle}:`, error);
                  return {
                    ...product,
                    variant_id: null,
                    price_html: this.formatMoney(product.price),
                    available: false,
                  };
                });
            });

            Promise.all(productPromises)
              .then((processedProducts) => {
                this.recommendations = processedProducts.filter((product) => product.variant_id);
                this.loading = false;
              })
              .catch((error) => {
                console.error('Error processing recommendations:', error);
                this.loading = false;
                this.recommendations = [];
              });
          } else {
            this.recommendations = [];
            this.loading = false;
          }
        })
        .catch((error) => {
          console.error('Error fetching recommendations:', error);
          this.loading = false;
          this.recommendations = [];
        });
    },
    formatMoney(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = parseInt(cents, 10) || 0;
      const dollars = Math.floor(value / 100);
      const cents_part = (value % 100).toString().padStart(2, '0');
      return `$${dollars}.${cents_part}`;
    },

    addToCart(variantId, quantity) {
      if (!variantId) {
        console.error('Cannot add to cart: Missing variant ID');
        return;
      }

      this.addingToCart = variantId;

      const data = {
        id: variantId,
        quantity: quantity,
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Cart add request failed with status ${response.status}`);
          }
          return response.json();
        })
        .then((item) => {
          // Refresh the cart
          return fetch('/cart.js');
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Cart fetch request failed with status ${response.status}`);
          }
          return response.json();
        })
        .then((cart) => {
          try {
            if (this.$parent && this.$parent.cart) {
              Object.assign(this.$parent.cart, cart);
            }

            if (this.$parent && typeof this.$parent.updateCartDrawer === 'function') {
              this.$parent.updateCartDrawer(cart);
            }

            const cartUpdateEvent = new CustomEvent('wetheme-cart-update', { detail: { cart } });
            document.documentElement.dispatchEvent(cartUpdateEvent);

            const shopifyCartEvent = new CustomEvent('cart:updated', { detail: { cart } });
            document.documentElement.dispatchEvent(shopifyCartEvent);
          } catch (error) {
            console.error('Error during cart update:', error);
          }
          this.addingToCart = null;
        })
        .catch((error) => {
          console.error('Error in cart operation:', error);
          this.addingToCart = null;
        });
    },
  },

  watch: {
    '$parent.cart': {
      handler(newCart) {
        if (newCart) {
          this.lastCartData = newCart;
          this.fetchRecommendations();
        }
      },
      deep: true,
    },
  },

  mounted() {
    this._toggleDrawerHandler = (event) => {
      if (event.detail && event.detail.open && event.detail.type === 'cart') {
        this.fetchRecommendations();
      }
    };

    this._cartUpdatedHandler = () => {
      this.fetchRecommendations();
    };

    this._wethemeCartUpdateHandler = (event) => {
      if (event.detail && event.detail.cart) {
        this.lastCartData = event.detail.cart;
        this.fetchRecommendations();
      }
    };

    document.documentElement.addEventListener('wetheme:toggleRightDrawer', this._toggleDrawerHandler);
    document.documentElement.addEventListener('cart:updated', this._cartUpdatedHandler);
    document.documentElement.addEventListener('wetheme-cart-update', this._wethemeCartUpdateHandler);

    if (this.$parent.cart) {
      this.lastCartData = this.$parent.cart;
      this.fetchRecommendations();
    }
  },

  beforeDestroy() {
    document.documentElement.removeEventListener('wetheme:toggleRightDrawer', this._toggleDrawerHandler);
    document.documentElement.removeEventListener('cart:updated', this._cartUpdatedHandler);
    document.documentElement.removeEventListener('wetheme-cart-update', this._wethemeCartUpdateHandler);

    this._toggleDrawerHandler = null;
    this._cartUpdatedHandler = null;
    this._wethemeCartUpdateHandler = null;
  },
};

export default CartDrawerRecommendations;

/**
 * Component Registration
 *
 * This self-executing function registers the component with the right drawer Vue instance.
 */
(function () {
  // Set a maximum number of retry attempts
  const MAX_RETRY_ATTEMPTS = 20;
  let retryCount = 0;

  function registerComponent() {
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      console.warn('Could not register cart drawer recommendations component after maximum retry attempts');
      return;
    }

    retryCount++;

    // Method 1: Try to find the Vue instance directly
    const rightDrawerElement = document.querySelector('.right-drawer-vue');
    if (!rightDrawerElement) {
      console.log('Right drawer element not found, retrying...');
      setTimeout(registerComponent, 500);
      return;
    }

    // Check if Vue is available on the element
    if (rightDrawerElement.__vue__) {
      const vueInstance = rightDrawerElement.__vue__;

      if (vueInstance.$children && vueInstance.$children.length > 0) {
        const rightDrawer = vueInstance.$children[0];

        if (rightDrawer) {
          console.log('Registering cart drawer recommendations component with Vue instance');
          rightDrawer.$options.components = rightDrawer.$options.components || {};
          rightDrawer.$options.components['cart-drawer-recommendations'] = CartDrawerRecommendations;

          document.documentElement.dispatchEvent(
            new CustomEvent('vue-components:registered', {
              detail: { components: ['cart-drawer-recommendations'] },
            })
          );
          return;
        }
      }
    }

    // Method 2: Try to use the global window.theme object if available
    if (window.theme && window.theme.rightDrawer) {
      console.log('Using global theme object to register component');
      const rightDrawer = window.theme.rightDrawer;

      if (rightDrawer.$options) {
        rightDrawer.$options.components = rightDrawer.$options.components || {};
        rightDrawer.$options.components['cart-drawer-recommendations'] = CartDrawerRecommendations;

        document.documentElement.dispatchEvent(
          new CustomEvent('vue-components:registered', {
            detail: { components: ['cart-drawer-recommendations'] },
          })
        );
        return;
      }
    }

    // If we get here, both methods failed, so retry
    console.log('Could not find Vue instance, retrying... (attempt ' + retryCount + ')');
    setTimeout(registerComponent, 500);
  }

  // Listen for the theme:ready event which indicates the right drawer is available
  document.documentElement.addEventListener('theme:ready', function () {
    console.log('Theme ready event detected, registering component');
    registerComponent();
  });

  // Try to register immediately in case the theme is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerComponent);
  } else {
    registerComponent();
  }
})();
